import { useLayoutEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { getCurrentHandle } from '../../reduxes/slices/current/selectors'
import HigherAutolinker from '../../util/higher_auto_linker'
import { stringToElement } from '../../util/string_to_element'
import { useIsAppRoute } from '../routes/use_is_app_route'

const MIMETYPE_REGEX =
  /\.(avi|bmp|csv|doc|docx|gif|jpg|jpeg|json|mp4|png|pdf|ppt|pptx|svg|txt|wav|webm|webp|xls|xlsx|xml|zip)$/

const REPRESENTABLE_BLOB_CLASSNAME = 'message-image-wrapper'

/**
 * Sanitises timeline html. Applies tags to anchors as required
 * @param timeline An object representing timeline data that contains an html field
 * @returns A qualified html string
 */
export const useSanitiseTimelineHtml = (timeline: { html: string }) => {
  const [qualifiedHtml, setQualifiedHtml] = useState<string>('')
  const isAppRoute = useIsAppRoute()
  const currentHandle = useSelector(getCurrentHandle)

  useLayoutEffect(() => {
    if (!timeline || !timeline.html) {
      return
    }

    // AutoLink the provided timeline html - this ensures that links are created, which can
    // then be properly processed later
    const autoLinkedHtml = HigherAutolinker.link(timeline.html)

    // Create an element from the auto linked html
    const htmlElement = stringToElement(autoLinkedHtml)

    // If the htmlElement is null, or is not an Element (ie: no querySelector*)
    // return raw html
    if (!htmlElement || !htmlElement.querySelectorAll) {
      setQualifiedHtml(autoLinkedHtml)
      return
    }

    const anchors = htmlElement.querySelectorAll('a')

    // When a link that points to the current hostname is provided, remove
    // its target, so its default behaviour changes to open in the same window
    // This is primarily to handle the case where a user pastes a Speakeasy link (topic, direct chat)
    // into a timeline and we want that link to open in the same window (or Electron app, for example)
    for (const anchor of Array.from(anchors)) {
      if (
        anchor.href.includes(':current_handle') &&
        new URL(anchor.href)?.hostname === window.location.hostname
      ) {
        anchor.href = anchor.href.replace(':current_handle', currentHandle)
      }

      const targetsMediaUrl = MIMETYPE_REGEX.test(anchor.href)
      if (
        anchor.classList.contains(REPRESENTABLE_BLOB_CLASSNAME) ||
        targetsMediaUrl
      ) {
        applyExternalAttributes(anchor)
      } else if (isAppRoute(anchor.href)) {
        // This is an internal link, so strip any external attributes
        anchor.removeAttribute('target')
        anchor.removeAttribute('rel')
        anchor.classList.remove('timeline__anchor')
      } else {
        applyExternalAttributes(anchor)
      }
    }

    // Set chatHtml with the final stringified HTML element
    setQualifiedHtml(htmlElement.outerHTML)
  }, [currentHandle, isAppRoute, timeline])

  return qualifiedHtml
}

const applyExternalAttributes = (anchor: HTMLElement) => {
  anchor.setAttribute('target', '_blank')
  anchor.setAttribute('rel', 'noopener noreferrer')
  anchor.classList.add('timeline__anchor')
}
